import {
  BaseResponsePaginationProps,
  BaseResponseProps,
  HeaderSection,
  generateFormRules,
  generateQueryString,
  getStandardError,
} from '@qlibs/react-components';
import {
  Alert,
  Card,
  Divider,
  Form,
  Grid,
  Input,
  Row,
  Select,
  Switch,
  message,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { backToList } from '../../helpers/listFilterQuery';
import {
  CustomFormItemPer1,
  CustomFormItemPer2,
  CustomFormItemPer3,
} from '../user/components/CustomFormItem';
import { httpRequest } from '../../helpers/api';
import {
  DeviceProperties,
  EDeviceType,
  ICreateDevice,
  resolveOriginalDeviceAssetId,
} from './types/device.type';
import SectionContent from '../../components/SectionContent';
import { AssetProperties } from '../asset/types/asset.type';
import { DeviceTypeProperties } from '../deviceType/types/device.type';
import { GroupProperties } from '../group/types/group.type';
import { CompanyProperties } from '../company/types/company.type';
import { EAEFuelType } from './Create';
import {
  getEntityDetailPath,
  isRamus2DeviceId,
  RAMUS2_READ_ONLY_MESSAGE,
} from '../../helpers/ramus2';
import {
  DeviceMobileActionButton,
  DeviceMobileAlert,
  DeviceMobileField,
  DeviceMobileForm,
  DeviceMobileHeaderActions,
  DeviceMobilePage,
  DeviceMobileResultCard,
  DeviceMobileSecondaryButton,
  DeviceMobileSection,
  DeviceMobileSectionHeader,
  DeviceMobileSwitchField,
  DeviceMobileWarningCard,
  MobileDeviceHeader,
} from './mobile';

interface ILocation {
  deviceId: string;
}

const DeviceEdit = () => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const { deviceId } = useParams<keyof ILocation>() as ILocation;
  const isReadOnlyRamus2 = isRamus2DeviceId(deviceId);
  const [isLoadingAction, setIsLoadingAction] = React.useState(false);
  const [data, setData] = React.useState<DeviceProperties>();
  const [form] = Form.useForm();
  const [assets, setAssets] = React.useState<AssetProperties[]>([]);
  const [companies, setCompanies] = React.useState<CompanyProperties[]>([]);
  const [groups, setGroups] = React.useState<GroupProperties[]>([]);
  const [activeGroupId, setActiveGroupId] = React.useState<string>();
  const [activeCompanyId, setActiveCompanyId] = React.useState<string>();
  const [deviceTypes, setDeviceTypes] = React.useState<DeviceTypeProperties[]>(
    [],
  );
  const [deviceUniqueId, setDeviceUniqueId] = useState<{
    asset: string;
    deviceType: string;
    location: string;
    flowType: string;
  }>({
    asset: '',
    deviceType: '',
    location: '',
    flowType: '',
  });
  const [currentDeviceType, setCurrentDeviceType] = useState<string>();
  const [aeFuelType, setAEFuelType] = React.useState<string>();
  const isPanelDoorDevice =
    currentDeviceType?.trim().toLowerCase() === 'panel door';

  useEffect(() => {
    if (!deviceId || !isReadOnlyRamus2) {
      return;
    }

    message.warning(RAMUS2_READ_ONLY_MESSAGE);
    navigate(getEntityDetailPath('device', deviceId), { replace: true });
  }, [deviceId, isReadOnlyRamus2, navigate]);

  async function updateDevice(values: ICreateDevice) {
    try {
      setIsLoadingAction(true);

      let metadata = {
        aeFuelType: values.aeFuelType,
        aeFuelConstanta: values.aeFuelConstanta
          ? Number(values.aeFuelConstanta)
          : null,
      };

      const selectedAsset = assets.find((item) => item.assetId === values.assetId);
      const formData = {
        name: values.name,
        uniqueId: deviceUniqueIdCombined,
        deviceTypeId: values.deviceType,
        assetId: resolveOriginalDeviceAssetId(selectedAsset, values.assetId),
        description: values.description,
        createdByUserId: values.createdByUserId,
        status: values.status ? 'active' : 'inactive',
        metadata,
      };

      await httpRequest.patch<BaseResponseProps<DeviceProperties>>(
        '/device/' + deviceId,
        formData,
      );

      message.success(`Success add ${values.name}`);
      form.resetFields();
      setData(undefined);
      setAEFuelType(undefined);
      backToList(navigate, '/device');
    } catch (error) {
      getStandardError(error, {
        triggerFormValidation: true,
        formRef: form,
        showToast: true,
      });
    } finally {
      setIsLoadingAction(false);
    }
  }

  const handleSubmit = async (values: any) => {
    if (data?.deletedAt) {
      message.warning('This data has been archived and cannot be edited');
    } else {
      if (deviceUniqueIdCombined) {
        const ids = deviceUniqueIdCombined.split('-');
        if (currentDeviceType === 'flowmeter') {
          if (ids.length < 4) {
            message.warning('Flowmeter device unique id must be valid');
            return;
          }
        } else if (currentDeviceType!.includes('ae')) {
          if (aeFuelType === EAEFuelType.FUEL_CALCULATED && ids.length < 4) {
            message.warning(
              `${currentDeviceType!.toLocaleUpperCase()} device ID must be valid. Please set the flow type`,
            );
            return;
          } else if (ids.length < 3) {
            message.warning(
              `${currentDeviceType!.toLocaleUpperCase()} device ID must be valid`,
            );
            return;
          }
        } else if (isPanelDoorDevice) {
          if (ids.length !== 3 || ids[1] !== 'dp' || ids[2] !== '1') {
            message.warning('Panel Door device unique id must use the format [asset]-dp-1');
            return;
          }
        } else if (['rpm', 'clutch'].includes(currentDeviceType!)) {
          if (ids.length < 3) {
            message.warning(
              `${currentDeviceType!.toLocaleUpperCase()} device ID must be valid`,
            );
            return;
          }
        } else {
          if (ids.length < 2) {
            message.warning(
              `${currentDeviceType!.toLocaleUpperCase()} device ID must be valid`,
            );
            return;
          }
        }

        updateDevice(values);
      } else {
        message.warning('Device Unique ID must be filled');
        return;
      }
    }
  };

  async function getDeviceTypes() {
    try {
      const res = await httpRequest.get<
        BaseResponsePaginationProps<DeviceTypeProperties>
      >('/device-type?filterStatus=active');

      if (res) {
        setDeviceTypes(res.data.payload.results);
      }
    } catch (error) {
      getStandardError(error, {
        triggerFormValidation: true,
        formRef: form,
        showToast: true,
      });
    } finally {
      setIsLoadingAction(false);
    }
  }

  useEffect(() => {
    if (isReadOnlyRamus2) {
      return;
    }

    const fetchAsset = async () => {
      try {
        setIsLoadingAction(true);
        const response = await httpRequest.get<
          BaseResponsePaginationProps<AssetProperties>
        >(
          `/asset${generateQueryString({
            filterCompanyIds: activeCompanyId,
          })}`,
        );

        if (response.data.payload) {
          setAssets(response.data.payload.results);
        }
      } catch (error) {
        message.error(`Failed get data assets`);
      } finally {
        setIsLoadingAction(false);
      }
    };

    if (activeCompanyId) {
      fetchAsset();
    }
    getDeviceTypes();
  }, [activeCompanyId, isReadOnlyRamus2]);

  const groupMapped = React.useMemo(() => {
    return groups.map((item) => ({
      value: item.groupId,
      label: item.name,
    }));
  }, [groups]);

  const companyMapped = React.useMemo(() => {
    return companies
      .map((item) => ({
        value: item.companyId,
        label: item.name,
        groupId: item.groupId,
      }))
      .filter((item) => item.groupId === activeGroupId);
  }, [companies, activeGroupId]);

  const assetMapped = React.useMemo(() => {
    return assets.map((item) => ({
      value: item.assetId,
      label: item.name,
    }));
  }, [assets, activeCompanyId]);

  const deviceTypeMapped = React.useMemo(() => {
    return Object.values(deviceTypes).map((item) => ({
      label: item.name,
      value: item.deviceTypeId,
    }));
  }, [deviceTypes]);

  useEffect(() => {
    if (isReadOnlyRamus2) {
      return;
    }

    const fetchGroup = async () => {
      try {
        setIsLoadingAction(true);
        const response =
          await httpRequest.get<BaseResponsePaginationProps<GroupProperties>>(
            '/group',
          );

        if (response.data.payload) {
          const resGroups = response.data.payload.results;
          setGroups(resGroups);

          let allCompanies: CompanyProperties[] = [];
          resGroups.forEach((item) => {
            if (item.companies) {
              allCompanies = [...allCompanies, ...item.companies];
            }
          });

          setCompanies(allCompanies);
          if (data && data.asset && data.asset.company) {
            const currGroupId = data.asset.company.groupId;
            const currCompanyId = data.asset.company.companyId;
            setActiveGroupId(currGroupId);
            setActiveCompanyId(currCompanyId);
            form.setFieldsValue({
              groupId: currGroupId,
              companyId: currCompanyId,
            });
          }
        }
      } catch (error) {
        message.error(`Failed get data groups`);
      } finally {
        setIsLoadingAction(false);
      }
    };

    if (deviceId && data && data.asset && data.asset.company) {
      fetchGroup();
    }
  }, [deviceId, data, isReadOnlyRamus2]);

  useEffect(() => {
    if (deviceId && !isReadOnlyRamus2) {
      async function fetchData(deviceId: string) {
        try {
          const response = await httpRequest.get<
            BaseResponseProps<DeviceProperties>
          >('/device/' + deviceId);
          if (response.data.payload) {
            const data = response.data.payload;

            setData(data);

            const formData = {
              ...data,
              deviceType: data.deviceTypeId,
              status: data.status === 'active' ? true : false,
              aeFuelConstanta:
                data.metadata && data.metadata.aeFuelConstanta
                  ? data.metadata.aeFuelConstanta
                  : null,
              aeFuelType: (data.metadata && data.metadata.aeFuelType) || null,
            };

            form.setFieldsValue(formData);

            // set flowmeter device unique ID
            if (
              data.deviceType.name.toLocaleLowerCase() ===
                EDeviceType.FLOWMETER ||
              data.deviceType.name
                .toLocaleLowerCase()
                .includes(EDeviceType.BUNKER)
            ) {
              const isBunkerDevice = data.deviceType.name
                .toLocaleLowerCase()
                .includes(EDeviceType.BUNKER);

              setCurrentDeviceType(data.deviceType.name);

              const ids = data.uniqueId.split('-');

              if (ids && ids.length) {
                if (isBunkerDevice) {
                  setDeviceUniqueId({
                    asset: ids[0],
                    deviceType: 'bunker',
                    location: '',
                    flowType: ids[2],
                  });
                } else {
                  setDeviceUniqueId({
                    asset: ids[0],
                    deviceType: 'fm',
                    location: ids[2],
                    flowType: ids[3],
                  });
                }
              }
            }

            // set rpm / clutch device unique ID
            if (
              [EDeviceType.RPM, EDeviceType.CLUTCH].includes(
                data.deviceType.name.toLocaleLowerCase() as EDeviceType,
              )
            ) {
              setCurrentDeviceType(data.deviceType.name);
              const ids = data.uniqueId.split('-');
              if (ids && ids.length) {
                setDeviceUniqueId({
                  asset: ids[0],
                  deviceType: data.deviceType.name.toLocaleLowerCase(),
                  location: ids[2],
                  flowType: '',
                });
              }
            }

            // set ae device unique ID
            if (
              data.deviceType.name.toLocaleLowerCase().includes(EDeviceType.AE)
            ) {
              setCurrentDeviceType(data.deviceType.name);

              if (data.metadata && data.metadata?.aeFuelType) {
                setAEFuelType(data.metadata?.aeFuelType);
              }

              const ids = data.uniqueId.split('-');
              if (ids && ids.length) {
                if (
                  data.metadata &&
                  data.metadata.aeFuelType &&
                  data.metadata.aeFuelType === EAEFuelType.FUEL_CALCULATED
                ) {
                  setAEFuelType(data.metadata.aeFuelType);
                  setDeviceUniqueId({
                    asset: ids[0],
                    deviceType: ids[1],
                    location: ids[2],
                    flowType: ids[3],
                  });
                } else {
                  setDeviceUniqueId({
                    asset: ids[0],
                    deviceType: ids[1],
                    location: ids[2],
                    flowType: '',
                  });
                }
              }
            }

            // set panel door device unique ID
            if (
              data.deviceType.name.trim().toLocaleLowerCase() === 'panel door'
            ) {
              setCurrentDeviceType(data.deviceType.name);
              const ids = data.uniqueId.split('-');
              setDeviceUniqueId({
                asset: ids[0],
                deviceType: 'dp',
                location: '1',
                flowType: '',
              });
            }

            // set gateway & gps device unique ID
            if (
              data.deviceType.name
                .toLocaleLowerCase()
                .includes(EDeviceType.GATEWAY) ||
              data.deviceType.name.toLocaleLowerCase().includes(EDeviceType.GPS)
            ) {
              setCurrentDeviceType(data.deviceType.name);
              const ids = data.uniqueId.split('-');
              if (ids && ids.length) {
                setDeviceUniqueId({
                  asset: ids[0],
                  deviceType: ids[1],
                  location: '',
                  flowType: '',
                });
              }
            }
          }
        } catch (error) {
          message.error(`Failed get device data`);
          console.error(error);
        } finally {
          setIsLoadingAction(false);
        }
      }

      fetchData(deviceId);
    }
  }, [deviceId, isReadOnlyRamus2]);

  const deviceUniqueIdCombined = useMemo(() => {
    if (deviceUniqueId) {
      return Object.values(deviceUniqueId)
        .filter((item) => item)
        .join('-');
    }
  }, [deviceUniqueId]);

  const prefix = useMemo(() => {
    if (isPanelDoorDevice) {
      return `${deviceUniqueId.asset}-dp`;
    }
    if (deviceUniqueId.deviceType.toLowerCase().includes('ae')) {
      return deviceUniqueId?.asset;
    }
    return deviceUniqueId?.asset + '-' + deviceUniqueId?.deviceType;
  }, [deviceUniqueId]);

  function handleChangeGroup(e: string) {
    const found = groupMapped.find((item) => item.value === e);
    if (found) {
      setActiveGroupId(found.value);
      form.setFieldsValue({
        companyId: undefined,
        assetId: undefined,
      });
      setDeviceUniqueId({
        asset: '',
        deviceType: '',
        location: '',
        flowType: '',
      });
    }
  }

  function handleChangeCompany(e: string) {
    const found = companyMapped.find((item) => item.value === e);
    if (found) {
      setActiveCompanyId(found.value);
      form.setFieldsValue({
        assetId: undefined,
      });
      setDeviceUniqueId({
        asset: '',
        deviceType: '',
        location: '',
        flowType: '',
      });
    }
  }

  function handleChangeAsset(e: string) {
    const found = assetMapped.find((item) => item.value === e);
    if (found) {
      setDeviceUniqueId((current) => ({
        ...current,
        asset: found.label.split(' ').join(''),
      }));
    }
  }

  function handleChangeDeviceType(e: string) {
    const found = deviceTypeMapped.find((item) => item.value === e);
    if (found) {
      setCurrentDeviceType(found.label.toLocaleLowerCase());
      if (found.label.toLocaleLowerCase() === 'flowmeter') {
        setDeviceUniqueId((current) => ({
          ...current,
          deviceType: 'fm',
        }));
      } else if (['bunker'].includes(found.label.toLocaleLowerCase())) {
        setDeviceUniqueId((current) => ({
          ...current,
          deviceType: 'bunker',
          location: '',
          flowType: '',
        }));
      } else if (
        ['rpm', 'clutch', 'ae'].includes(found.label.toLocaleLowerCase())
      ) {
        setDeviceUniqueId((current) => ({
          ...current,
          deviceType: found.label.toLocaleLowerCase(),
          flowType: '',
        }));
      } else {
        setDeviceUniqueId((current) => ({
          ...current,
          deviceType: found.label.toLocaleLowerCase(),
          location: '',
          flowType: '',
        }));
      }
    }
  }

  function handleChangeAEFuelType(e: string) {
    setAEFuelType(e);
    if (e === EAEFuelType.FUEL_CONSTANTA) {
      setDeviceUniqueId((current) => ({
        ...current,
        flowType: '',
      }));
    }
  }

  const renderDeviceUniqueID = () => {
    if (
      currentDeviceType?.toLowerCase() &&
      currentDeviceType?.toLowerCase() === EDeviceType.FLOWMETER
    ) {
      return (
        <Row>
          <CustomFormItemPer3 label='Prefix'>
            <div>{prefix}</div>
          </CustomFormItemPer3>
          <CustomFormItemPer3 label='Location'>
            <Select
              disabled={data?.deletedAt ? true : false}
              value={deviceUniqueId?.location}
              placeholder='Choose location'
              style={{ width: '100%' }}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, location: e }))
              }
              options={[
                {
                  label: 'PORT',
                  value: '1',
                },
                {
                  label: 'STARBOARD',
                  value: '2',
                },
                {
                  label: 'CENTER',
                  value: '3',
                },
              ]}
            />
          </CustomFormItemPer3>
          <CustomFormItemPer3 label='Flow type'>
            <Select
              disabled={data?.deletedAt ? true : false}
              placeholder='Choose flow type'
              style={{ width: '100%' }}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, flowType: e }))
              }
              value={deviceUniqueId?.flowType}
              options={[
                {
                  label: 'IN',
                  value: '1',
                },
                {
                  label: 'OUT',
                  value: '2',
                },
              ]}
            />
          </CustomFormItemPer3>
        </Row>
      );
    } else if (
      currentDeviceType?.toLowerCase() &&
      currentDeviceType?.toLowerCase() === EDeviceType.BUNKER
    ) {
      return (
        <Row>
          <CustomFormItemPer3 label='Prefix'>
            <div>{prefix}</div>
          </CustomFormItemPer3>
          <CustomFormItemPer3 label='Flow type'>
            <Select
              disabled={data?.deletedAt ? true : false}
              placeholder='Choose flow type'
              style={{ width: '100%' }}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, flowType: e }))
              }
              value={deviceUniqueId?.flowType}
              options={[
                {
                  label: 'IN',
                  value: '1',
                },
                {
                  label: 'OUT',
                  value: '2',
                },
              ]}
            />
          </CustomFormItemPer3>
        </Row>
      );
    } else if (
      currentDeviceType?.toLowerCase() &&
      ['rpm', 'clutch'].includes(currentDeviceType?.toLowerCase())
    ) {
      return (
        <Row>
          <CustomFormItemPer3 label='Prefix'>
            <div>{prefix}</div>
          </CustomFormItemPer3>
          <CustomFormItemPer3 label='Location'>
            <Select
              disabled={data?.deletedAt ? true : false}
              value={deviceUniqueId?.location}
              placeholder='Choose location'
              style={{ width: '100%' }}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, location: e }))
              }
              options={[
                {
                  label: 'PORT',
                  value: '1',
                },
                {
                  label: 'STARBOARD',
                  value: '2',
                },
                {
                  label: 'CENTER',
                  value: '3',
                },
              ]}
            />
          </CustomFormItemPer3>
        </Row>
      );
    } else if (
      currentDeviceType &&
      currentDeviceType.toLowerCase().includes(EDeviceType.AE) &&
      aeFuelType === EAEFuelType.FUEL_CALCULATED
    ) {
      return (
        <Row>
          <CustomFormItemPer2 label='Prefix'>
            <Input value={prefix} disabled />
          </CustomFormItemPer2>
          <CustomFormItemPer2 label='Flow type'>
            <Select
              placeholder='Choose asset'
              style={{ width: '100%' }}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, flowType: e }))
              }
              value={deviceUniqueId?.flowType}
              options={[
                {
                  label: 'IN',
                  value: '1',
                },
                {
                  label: 'OUT',
                  value: '2',
                },
              ]}
            />
          </CustomFormItemPer2>
        </Row>
      );
    } else {
      return;
    }
  };

  const renderMobileDeviceUniqueID = () => {
    if (
      currentDeviceType?.toLowerCase() &&
      currentDeviceType?.toLowerCase() === EDeviceType.FLOWMETER
    ) {
      return (
        <>
          <DeviceMobileField label="Prefix">
            <Input value={prefix} disabled />
          </DeviceMobileField>
          <DeviceMobileField label="Location">
            <Select
              disabled={data?.deletedAt ? true : false}
              value={deviceUniqueId?.location}
              placeholder="Choose location"
              options={[
                { label: 'PORT', value: '1' },
                { label: 'STARBOARD', value: '2' },
                { label: 'CENTER', value: '3' },
              ]}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, location: e }))
              }
            />
          </DeviceMobileField>
          <DeviceMobileField label="Flow type">
            <Select
              disabled={data?.deletedAt ? true : false}
              value={deviceUniqueId?.flowType}
              placeholder="Choose flow type"
              options={[
                { label: 'IN', value: '1' },
                { label: 'OUT', value: '2' },
              ]}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, flowType: e }))
              }
            />
          </DeviceMobileField>
        </>
      );
    }

    if (
      currentDeviceType?.toLowerCase() &&
      currentDeviceType?.toLowerCase() === EDeviceType.BUNKER
    ) {
      return (
        <>
          <DeviceMobileField label="Prefix">
            <Input value={prefix} disabled />
          </DeviceMobileField>
          <DeviceMobileField label="Flow type">
            <Select
              disabled={data?.deletedAt ? true : false}
              value={deviceUniqueId?.flowType}
              placeholder="Choose flow type"
              options={[
                { label: 'IN', value: '1' },
                { label: 'OUT', value: '2' },
              ]}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, flowType: e }))
              }
            />
          </DeviceMobileField>
        </>
      );
    }

    if (
      currentDeviceType?.toLowerCase() &&
      ['rpm', 'clutch'].includes(currentDeviceType?.toLowerCase())
    ) {
      return (
        <>
          <DeviceMobileField label="Prefix">
            <Input value={prefix} disabled />
          </DeviceMobileField>
          <DeviceMobileField label="Location">
            <Select
              disabled={data?.deletedAt ? true : false}
              value={deviceUniqueId?.location}
              placeholder="Choose location"
              options={[
                { label: 'PORT', value: '1' },
                { label: 'STARBOARD', value: '2' },
                { label: 'CENTER', value: '3' },
              ]}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, location: e }))
              }
            />
          </DeviceMobileField>
        </>
      );
    }

    if (
      currentDeviceType &&
      currentDeviceType.toLowerCase().includes(EDeviceType.AE) &&
      aeFuelType === EAEFuelType.FUEL_CALCULATED
    ) {
      return (
        <>
          <DeviceMobileField label="Prefix">
            <Input value={prefix} disabled />
          </DeviceMobileField>
          <DeviceMobileField label="Flow type">
            <Select
              value={deviceUniqueId?.flowType}
              placeholder="Choose flow type"
              options={[
                { label: 'IN', value: '1' },
                { label: 'OUT', value: '2' },
              ]}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, flowType: e }))
              }
            />
          </DeviceMobileField>
        </>
      );
    }

    return null;
  };

  const renderMobileContent = () => (
    <DeviceMobilePage>
      <MobileDeviceHeader
        title="Device"
        onBack={() => backToList(navigate, '/device')}
        action={
          <DeviceMobileHeaderActions>
            <DeviceMobileSecondaryButton onClick={() => backToList(navigate, '/device')}>
              Cancel
            </DeviceMobileSecondaryButton>
            <DeviceMobileActionButton
              type="primary"
              loading={isLoadingAction}
              disabled={data?.deletedAt ? true : false}
              onClick={() => form.submit()}
            >
              Save
            </DeviceMobileActionButton>
          </DeviceMobileHeaderActions>
        }
      />

      <DeviceMobileForm>
        {data?.deletedAt ? (
          <DeviceMobileAlert>
            <Alert
              type="warning"
              message="This data has been archived and cannot be edited"
            />
          </DeviceMobileAlert>
        ) : (
          false
        )}
        <Form
          form={form}
          name="profileForm"
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
          disabled={data?.deletedAt ? true : false}
        >
          <DeviceMobileSection $bordered>
            <DeviceMobileSectionHeader>
              <h2>Information</h2>
              <p>Detail information about device</p>
            </DeviceMobileSectionHeader>
            <DeviceMobileField
              label="Group"
              name="groupId"
              rules={generateFormRules('Group', ['required'])}
            >
              <Select
                loading={isLoadingAction}
                disabled={data?.deletedAt ? true : isLoadingAction}
                showSearch
                placeholder="Choose group"
                options={groupMapped}
                onChange={handleChangeGroup}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </DeviceMobileField>
            <DeviceMobileField
              label="Company"
              name="companyId"
              rules={generateFormRules('Company', ['required'])}
            >
              <Select
                loading={isLoadingAction}
                disabled={data?.deletedAt ? true : isLoadingAction}
                showSearch
                placeholder="Choose company"
                options={companyMapped}
                onChange={handleChangeCompany}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </DeviceMobileField>
            <DeviceMobileField
              label="Asset"
              name="assetId"
              rules={generateFormRules('Asset', ['required'])}
            >
              <Select
                loading={isLoadingAction}
                disabled={isLoadingAction}
                showSearch
                placeholder="Choose asset"
                options={assetMapped}
                onChange={handleChangeAsset}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </DeviceMobileField>
            <DeviceMobileField
              label="Device Type"
              name="deviceType"
              rules={generateFormRules('Device Phone Number', ['required'])}
            >
              <Select
                disabled={data?.deletedAt ? true : false}
                showSearch
                placeholder="Choose device type"
                options={deviceTypeMapped}
                onChange={handleChangeDeviceType}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </DeviceMobileField>
            <DeviceMobileField
              label="Device Name"
              name="name"
              rules={generateFormRules('Device Name', ['required'])}
            >
              <Input placeholder="Input device name" />
            </DeviceMobileField>
            <DeviceMobileField label="Description" name="description">
              <Input placeholder="Input device description" />
            </DeviceMobileField>
            <DeviceMobileField label="AE Fuel Type" name="aeFuelType">
              <Select
                loading={isLoadingAction}
                disabled={isLoadingAction}
                showSearch
                placeholder="Choose device type"
                options={Object.values(EAEFuelType).map((item) => ({
                  label: item.replace(/-/g, ' ').toUpperCase(),
                  value: item,
                }))}
                onChange={handleChangeAEFuelType}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </DeviceMobileField>
            {currentDeviceType?.toLowerCase() &&
              currentDeviceType?.toLowerCase().includes(EDeviceType.AE) &&
              aeFuelType === EAEFuelType.FUEL_CONSTANTA && (
                <DeviceMobileField
                  label="AE Fuel Constanta"
                  name="aeFuelConstanta"
                >
                  <Input placeholder="Input AE fuel constanta" suffix="L/Min" />
                </DeviceMobileField>
              )}
            {currentDeviceType?.toLowerCase() &&
              currentDeviceType?.toLowerCase().includes(EDeviceType.AE) && (
                <DeviceMobileAlert>
                  <Alert
                    type="warning"
                    showIcon
                    description={
                      <>
                        <div>
                          AE Fuel Constanta only required when AE Fuel Type is
                          set to Fuel Constanta
                        </div>
                        <div>
                          If you choose fuel calculated then you need to set the
                          flow
                        </div>
                      </>
                    }
                  />
                </DeviceMobileAlert>
              )}
          </DeviceMobileSection>

          <DeviceMobileSection $bordered>
            <DeviceMobileSectionHeader>
              <h2>Device Unique ID</h2>
              <p>Detail information about device</p>
            </DeviceMobileSectionHeader>
            {renderMobileDeviceUniqueID()}
            <DeviceMobileResultCard>
              Result Device Unique ID: {deviceUniqueIdCombined}
            </DeviceMobileResultCard>
            <DeviceMobileWarningCard>
              Please makesure this device unique id match with ID on IOT.
              Otherwise the report data won't work
            </DeviceMobileWarningCard>
          </DeviceMobileSection>

          <DeviceMobileSection>
            <DeviceMobileSectionHeader>
              <h2>Setting</h2>
              <p>These are device information, you can change anything</p>
            </DeviceMobileSectionHeader>
            <DeviceMobileSwitchField
              label="Status"
              name="status"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch defaultChecked className="custom-switch" />
            </DeviceMobileSwitchField>
          </DeviceMobileSection>
        </Form>
      </DeviceMobileForm>
    </DeviceMobilePage>
  );

  if (isReadOnlyRamus2) {
    return null;
  }

  if (isMobile) {
    return renderMobileContent();
  }

  return (
    <React.Fragment>
      <HeaderSection
        icon='back'
        title={'Update Device'}
        subtitle={'Update current device'}
        showCancelButton
        showSaveButton
        isLoading={isLoadingAction}
        onSave={() => form.submit()}
      />

      <Card>
        {data?.deletedAt ? (
          <Alert
            className='mb-5'
            type='warning'
            message='This data has been archived and cannot be edited'
          />
        ) : (
          false
        )}
        <Form
          form={form}
          name='profileForm'
          layout='vertical'
          onFinish={handleSubmit}
          autoComplete='off'
          disabled={data?.deletedAt ? true : false}
        >
          <SectionContent
            groupTitle='Information'
            subTitle='Detail information about device'
          >
            <Row>
              <CustomFormItemPer1
                label='Group'
                name='groupId'
                rules={generateFormRules('Group', ['required'])}
              >
                <Select
                  loading={isLoadingAction}
                  disabled={data?.deletedAt ? true : isLoadingAction}
                  showSearch
                  placeholder='Choose group'
                  style={{ width: '100%' }}
                  options={groupMapped}
                  onChange={handleChangeGroup}
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </CustomFormItemPer1>
            </Row>
            <Row>
              <CustomFormItemPer2
                label='Company'
                name='companyId'
                rules={generateFormRules('Company', ['required'])}
              >
                <Select
                  loading={isLoadingAction}
                  disabled={data?.deletedAt ? true : isLoadingAction}
                  showSearch
                  placeholder='Choose company'
                  style={{ width: '100%' }}
                  options={companyMapped}
                  onChange={handleChangeCompany}
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </CustomFormItemPer2>
              <CustomFormItemPer2
                label='Asset'
                name='assetId'
                rules={generateFormRules('Asset', ['required'])}
              >
                <Select
                  loading={isLoadingAction}
                  disabled={isLoadingAction}
                  showSearch
                  placeholder='Choose asset'
                  style={{ width: '100%' }}
                  options={assetMapped}
                  onChange={handleChangeAsset}
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </CustomFormItemPer2>
            </Row>
            <Row>
              <CustomFormItemPer2
                label='Device Type'
                name='deviceType'
                rules={generateFormRules('Device Phone Number', ['required'])}
              >
                <Select
                  disabled={data?.deletedAt ? true : false}
                  showSearch
                  placeholder='Choose device type'
                  style={{ width: '100%' }}
                  options={deviceTypeMapped}
                  onChange={handleChangeDeviceType}
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </CustomFormItemPer2>
              <CustomFormItemPer2
                label='Device Name'
                name='name'
                rules={generateFormRules('Device Name', ['required'])}
              >
                <Input placeholder='Input device name' />
              </CustomFormItemPer2>
            </Row>
            <CustomFormItemPer1 label='Description' name='description'>
              <Input placeholder='Input device description' />
            </CustomFormItemPer1>
            <Row>
              <CustomFormItemPer2 label='AE Fuel Type' name='aeFuelType'>
                <Select
                  loading={isLoadingAction}
                  disabled={isLoadingAction}
                  showSearch
                  placeholder='Choose device type'
                  style={{ width: '100%' }}
                  options={Object.values(EAEFuelType).map((item) => ({
                    label: item.replace(/-/g, ' ').toUpperCase(),
                    value: item,
                  }))}
                  onChange={handleChangeAEFuelType}
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </CustomFormItemPer2>
              {currentDeviceType?.toLowerCase() &&
                currentDeviceType?.toLowerCase().includes(EDeviceType.AE) &&
                aeFuelType === EAEFuelType.FUEL_CONSTANTA && (
                  <CustomFormItemPer2
                    label='AE Fuel Constanta'
                    name='aeFuelConstanta'
                  >
                    <Input
                      placeholder='Input AE fuel constanta'
                      suffix='L/Min'
                    />
                  </CustomFormItemPer2>
                )}

              {currentDeviceType?.toLowerCase() &&
                currentDeviceType?.toLowerCase().includes(EDeviceType.AE) && (
                  <CustomFormItemPer1>
                    <Alert
                      type='warning'
                      showIcon
                      description={
                        <>
                          <div>
                            AE Fuel Constanta only required when AE Fuel Type is
                            set to Fuel Constanta
                          </div>
                          <div>
                            If you choose fuel calculated then you need to set
                            the flow
                          </div>
                        </>
                      }
                    />
                  </CustomFormItemPer1>
                )}
            </Row>
          </SectionContent>

          <Divider />

          <SectionContent
            groupTitle='Device Unique ID'
            subTitle='Detail information about device'
          >
            {renderDeviceUniqueID()}
            <Row>
              <CustomFormItemPer1>
                <Alert
                  message={`Result Device Unique ID: ${deviceUniqueIdCombined}`}
                />
              </CustomFormItemPer1>
            </Row>
          </SectionContent>

          <Divider />

          <SectionContent
            groupTitle={`Setting`}
            subTitle='These are device information, you can change anything'
          >
            <CustomFormItemPer1
              label='Status'
              name='status'
              valuePropName='checked'
              initialValue={true}
            >
              <Switch defaultChecked className='custom-switch' />
            </CustomFormItemPer1>
          </SectionContent>
        </Form>
      </Card>
    </React.Fragment>
  );
};

export default DeviceEdit;
